/**
 * CitrusQuiz - Main Quiz Component
 * 
 * A PuraU-style interactive quiz for finding perfect citrus trees.
 * Designed to be embedded as a standalone widget on Shopify.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  QuizState,
  initialQuizState,
  quizQuestions,
  processAnswer,
  nextStep,
  prevStep,
  getRecommendations,
  buildKlaviyoPayload,
  resolveQuizResult,
  TreeScore,
  KlaviyoQuizPayload,
} from '../../utils/citrusQuizLogic';
import { CitrusTree, citrusTrees } from '../../data/citrusTrees';
import {
  FreshProductMetadata,
  fetchFreshProductMetadata,
  getFreshProductMetadata,
} from '../../services/shopifyImageService';
import {
  CITRUS_COUNTER_START,
  CITRUS_COUNTER_SCHEDULE,
  pickCitrusCelebrationLine,
  type CitrusCounterStep,
} from '../../data/citrusQuizUxCopy';
import QuizSlide from './QuizSlide';
import EmpathyScreen from './EmpathyScreen';
import EmailGate from './EmailGate';
import ResultsGrid from './ResultsGrid';
import ProgressBar from './ProgressBar';
import CitrusReadingCounter from './CitrusReadingCounter';
import CitrusRevealBuildUp from './CitrusRevealBuildUp';
import QuizAcknowledgment from '../QuizAcknowledgment';
import './CitrusQuiz.css';

// Extend Window interface to include our reference counter
declare global {
  interface Window {
    __citrusQuizActiveCount?: number;
  }
}

interface CitrusQuizProps {
  onComplete?: ((payload: KlaviyoQuizPayload) => void) | undefined;
  apiEndpoint?: string | undefined;
}

/** Reading-counter display state (UX-5). */
interface CounterState {
  count: number;
  narration: string | null;
  delta: number | null;
  tick: number;
}

const CitrusQuiz: React.FC<CitrusQuizProps> = ({
  onComplete,
  apiEndpoint = '/api/quiz/submit'
}) => {
  const [state, setState] = useState<QuizState>(initialQuizState);
  const [recommendations, setRecommendations] = useState<TreeScore[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ackLine, setAckLine] = useState<string | null>(null);
  const [counter, setCounter] = useState<CounterState>({
    count: CITRUS_COUNTER_START.count,
    narration: CITRUS_COUNTER_START.narration,
    delta: null,
    tick: 0,
  });

  // Celebration lines never repeat in a session; seeded with the counter
  // narration lines so the same words never show in two places at once.
  const usedLinesRef = useRef<Set<string> | null>(null);
  if (usedLinesRef.current === null) {
    usedLinesRef.current = new Set<string>([
      CITRUS_COUNTER_START.narration,
      ...Object.values(CITRUS_COUNTER_SCHEDULE).map((s) => s.narration),
    ]);
  }

  // Fresh product metadata from Shopify - pre-fetched on mount so it's ready by results time.
  // Why this matters: Starts fetching current product photos + review social proof immediately
  // while the user answers questions, so the final recommendations can rank and render with
  // live storefront metadata without adding a results-page delay.
  const freshProductMetadataRef = useRef<Record<string, FreshProductMetadata>>({});

  /**
   * Mobile scroll fix - Add body class to enable scrolling on quiz page
   * Why this matters: Mobile browsers need explicit overflow:auto on html/body
   * to allow touch scrolling. The class 'citrus-quiz-active' triggers CSS that
   * overrides any global overflow:hidden that might block scroll.
   * 
   * Reference counting: Multiple CitrusQuiz instances can exist simultaneously,
   * so we use a reference counter to only remove the class when all instances
   * have unmounted. This prevents scrolling from being broken when one instance
   * unmounts while another is still active.
   */
  useEffect(() => {
    // Initialize reference counter if needed
    if (!window.__citrusQuizActiveCount) {
      window.__citrusQuizActiveCount = 0;
    }
    
    // Increment counter and add class on mount
    window.__citrusQuizActiveCount++;
    document.body.classList.add('citrus-quiz-active');
    document.documentElement.classList.add('citrus-quiz-active');
    
    // Cleanup on unmount - only remove class if no other instances are active
    return () => {
      // Guard check for TypeScript - counter should always exist at cleanup
      if (typeof window.__citrusQuizActiveCount === 'number') {
        window.__citrusQuizActiveCount--;
        if (window.__citrusQuizActiveCount <= 0) {
          document.body.classList.remove('citrus-quiz-active');
          document.documentElement.classList.remove('citrus-quiz-active');
          window.__citrusQuizActiveCount = 0;
        }
      }
    };
  }, []);

  /**
   * Pre-fetch fresh product metadata from Shopify on quiz mount.
   * Why this matters: By the time the user finishes the quiz, fresh image URLs
   * and live review stats are already cached. If the fetch fails, we gracefully
   * fall back to the hardcoded values in citrusTrees.ts — no visible error.
   */
  useEffect(() => {
    Promise.resolve(fetchFreshProductMetadata()).then((metadata) => {
      freshProductMetadataRef.current = metadata || {};
    });
  }, []);

  const currentQuestion = quizQuestions[state.currentStep];
  const totalSteps = quizQuestions.filter((question) => question.countsTowardProgress).length;
  const currentProgressStep = quizQuestions
    .slice(0, state.currentStep + 1)
    .filter((question) => question.countsTowardProgress).length;

  // Handle answer selection
  const handleAnswer = useCallback((selectedOptions: string[], textValue?: string) => {
    if (!currentQuestion) return;

    setState((previousState) =>
      processAnswer(previousState, currentQuestion.id, selectedOptions, textValue)
    );
  }, [currentQuestion]);

  /**
   * Merge live Shopify metadata into the static tree catalog before scoring.
   * Why this matters: Recommendation ranking should respect live review counts and
   * sold-out state, not just display them after the fact. This keeps both scoring
   * and rendering aligned with the current storefront.
   */
  const buildLiveTreeCatalog = useCallback((): CitrusTree[] => {
    const freshMetadata = freshProductMetadataRef.current;
    if (!freshMetadata || Object.keys(freshMetadata).length === 0) {
      return citrusTrees;
    }

    return citrusTrees.map((tree) => {
      const liveMetadata = getFreshProductMetadata(tree.slug, freshMetadata);
      if (!liveMetadata) {
        return tree;
      }

      const mergedTree: CitrusTree = {
        ...tree,
        imageUrl: liveMetadata.imageUrl || tree.imageUrl,
        reviews: liveMetadata.reviews ?? tree.reviews,
        isAvailable: liveMetadata.isAvailable,
      };

      // Why this matters: exactOptionalPropertyTypes allows "rating" to be absent,
      // but not explicitly set to undefined. Only attach it when we have a real number.
      if (liveMetadata.rating !== null && liveMetadata.rating !== undefined) {
        mergedTree.rating = liveMetadata.rating;
      }

      return mergedTree;
    });
  }, []);

  // Submit quiz to backend
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Calculate recommendations using live Shopify metadata when available.
      const recs = getRecommendations(state, buildLiveTreeCatalog());
      setRecommendations(recs);

      // Build payload after lazy-loading Klaviyo-only snippet copy.
      // Why this matters: the quiz UI stays light until the user actually submits.
      const payload = await buildKlaviyoPayload(state, recs);

      // Submit to backend
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit quiz ');
      }

      // Show results
      setShowResults(true);
      
      // Call onComplete callback if provided
      if (onComplete) {
        onComplete(payload);
      }
    } catch (err) {
      console.error('Quiz submission error:', err);
      setError('Something went wrong. Please try again.');
      
      // Still show results even if API fails — live metadata stays applied locally
      const recs = getRecommendations(state, buildLiveTreeCatalog());
      setRecommendations(recs);
      setShowResults(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [state, apiEndpoint, onComplete, buildLiveTreeCatalog]);

  // Handle continue button
  const handleContinue = useCallback(() => {
    if (currentQuestion?.id === 'email' && state.email) {
      handleSubmit();
      return;
    }

    setState((previousState) => nextStep(previousState));
  }, [state.email, currentQuestion, handleSubmit]);

  /**
   * UX-1 + UX-5: called by the slide the moment an answer is submitted.
   * Picks a never-repeated celebration line and ticks the reading counter
   * down while the acknowledgment is still on screen.
   */
  const handleAcknowledge = useCallback(() => {
    if (!currentQuestion) return '';
    const used = usedLinesRef.current as Set<string>;
    const line = pickCitrusCelebrationLine(currentQuestion.id, used);
    used.add(line);

    const scheduled = CITRUS_COUNTER_SCHEDULE[currentQuestion.id];
    if (scheduled) {
      setCounter((prev) =>
        scheduled.count < prev.count
          ? {
              count: scheduled.count,
              narration: scheduled.narration,
              delta: prev.count - scheduled.count,
              tick: prev.tick + 1,
            }
          : prev
      );
    }
    return line;
  }, [currentQuestion]);

  /**
   * Counter value as of arriving at a step: the schedule entry of the last
   * scored question before it, or the 120 opener. Used to snap (not animate)
   * the chip when the user navigates Back and re-answers.
   */
  const expectedCounterAtStep = useCallback((stepIndex: number): CitrusCounterStep => {
    for (let i = Math.min(stepIndex, quizQuestions.length) - 1; i >= 0; i--) {
      const scheduled = CITRUS_COUNTER_SCHEDULE[quizQuestions[i].id];
      if (scheduled) return scheduled;
    }
    return CITRUS_COUNTER_START;
  }, []);

  /**
   * Apply every Back action to React's latest state snapshot.
   * Why this matters: rapid clicks may be batched, so reading the render-time
   * state here can collapse multiple Back actions into one.
   */
  const handleBack = useCallback(() => {
    if (showResults) {
      setShowResults(false);
      return;
    }

    setState((currentState) => {
      const previousState = prevStep(currentState);
      // Snap the counter (no tick animation) to where it stood before this step.
      const expected = expectedCounterAtStep(previousState.currentStep);
      setCounter((prev) =>
        prev.count === expected.count
          ? prev
          : {
              count: expected.count,
              narration:
                expected.count === CITRUS_COUNTER_START.count
                  ? CITRUS_COUNTER_START.narration
                  : expected.narration,
              delta: null,
              tick: prev.tick,
            }
      );
      return previousState;
    });
  }, [showResults, expectedCounterAtStep]);

  useEffect(() => {
    if (!currentQuestion?.autoAdvanceMs || showResults) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setState((previousState) => nextStep(previousState));
    }, currentQuestion.autoAdvanceMs);

    return () => window.clearTimeout(timeout);
  }, [currentQuestion, showResults]);

  // Check if current question has valid answer
  const hasValidAnswer = useCallback((): boolean => {
    if (!currentQuestion) return false;

    if (currentQuestion.type === 'screen' || currentQuestion.type === 'loading') return true;

    if (currentQuestion.type === 'text') {
      if (currentQuestion.id === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(state.email);
      }
    }

    if ((currentQuestion.minSelections ?? 1) === 0) {
      return true;
    }

    const answer = state.answers.find(a => a.questionId === currentQuestion.id);
    if (!answer) return false;

    const minSelections = currentQuestion.minSelections || 1;
    return answer.selectedOptions.length >= minSelections;
  }, [currentQuestion, state]);

  // Helper to personalize text with user's name
  const personalize = useCallback((text: string): string => {
    if (!text) return text;
    return text.replace(/\{\{name\}\}/g, state.userName || 'friend');
  }, [state.userName]);

  // If showing results
  if (showResults) {
    const result = resolveQuizResult(state, recommendations);
    return (
      <div className="citrus-quiz citrus-quiz-results">
        <ResultsGrid
          userName={state.userName}
          result={result}
          onBack={handleBack}
        />
      </div>
    );
  }

  // If no current question (shouldn't happen)
  if (!currentQuestion) {
    return <div className="citrus-quiz-error">Quiz loading...</div>;
  }

  return (
    <div className={`citrus-quiz`}>
      <div className="citrus-quiz-container">
        <div className="citrus-quiz-progress-area">
          {/* Progress Bar */}
          <ProgressBar
            current={Math.min(currentProgressStep, totalSteps)}
            total={totalSteps}
            bartype='full'
          />
          {/* Why this matters: an empty optional line still needs a visible
              kicker/progress card during the 2.5-second interaction lock. */}
          {ackLine !== null && (
            <QuizAcknowledgment line={ackLine} kicker="Your grove just narrowed" />
          )}
          {/* UX-5: the reading counter remains in the same top progress group. */}
          <CitrusReadingCounter
            count={counter.count}
            narration={counter.narration}
            delta={counter.delta}
            tick={counter.tick}
          />
        </div>

        {/* Quiz Content */}
        <div className="citrus-quiz-content">
          {currentQuestion.type === 'screen' ? (
            <EmpathyScreen
              key={state.currentStep}
              screenId={currentQuestion.id}
              caption={currentQuestion.caption}
              headline={personalize(currentQuestion.title)}
              body={personalize(currentQuestion.subtitle || '')}
              description={personalize(currentQuestion.description || '')}
              onContinue={handleContinue}
              isTapToContinue={!currentQuestion.buttonLabel}
              onBack={state.currentStep > 0 ? handleBack : undefined}
              {...(currentQuestion.buttonLabel
                ? { buttonLabel: currentQuestion.buttonLabel }
                : {})}
            />
          ) : currentQuestion.type === 'loading' ? (
            // UX-6: staged pre-reveal build-up between the last answer and the gate.
            <CitrusRevealBuildUp key={currentQuestion.id} onComplete={handleContinue} />
          ) : currentQuestion.id === 'email' ? (
            <EmailGate
              userName={state.userName}
              caption={currentQuestion.caption}
              title={currentQuestion.title}
              description={currentQuestion.subtitle || ''}
              email={state.email}
              onEmailChange={(email) => handleAnswer([], email)}
              onSubmit={handleSubmit}
              onBack={handleBack}
              isSubmitting={isSubmitting}
              error={error}
              {...(currentQuestion.helperText
                ? { helperText: currentQuestion.helperText }
                : {})}
              {...(currentQuestion.buttonLabel
                ? { buttonLabel: currentQuestion.buttonLabel }
                : {})}
            />
          ) : (
            <QuizSlide
            question={currentQuestion}
            state={state}
            onAnswer={handleAnswer}
            onContinue={handleContinue}
            onBack={state.currentStep > 0 ? handleBack : undefined}
            canContinue={hasValidAnswer()}
            onAcknowledge={handleAcknowledge}
            onAcknowledgmentChange={setAckLine}
          />
          )}
        </div>

        {/* US Citrus Branding */}
        <div className="citrus-quiz-branding">
          <img 
            src="https://uscitrusnursery.com/cdn/shop/files/US_CITRUS_NURSERY_Logo_9dc87bb3-7ec6-431e-b009-e3cefe1ee433.png?crop=center&height=32&v=1766440184&width=32" 
            alt="US Citrus Nursery"
            className="citrus-quiz-logo"
          />
          <span>Powered by US Citrus Nursery</span>
        </div>
      </div>
    </div>
  );
};

export default CitrusQuiz;
